import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Check, Loader2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export default function WaitlistForm({ productId, compact = false }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle') // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!email) return

    setStatus('loading')
    setErrorMessage('')

    try {
      const { error } = await supabase
        .from('product_waitlist')
        .insert({ email, product_id: productId })

      if (error) {
        if (error.code === '23505') {
          // Duplicate entry
          setErrorMessage("You're already on the list!")
          setStatus('error')
        } else {
          throw error
        }
      } else {
        setStatus('success')
        setEmail('')
      }
    } catch (error) {
      console.error('Waitlist signup error:', error)
      setErrorMessage('Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'} text-pp-sage`}
      >
        <Check className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        <span>You're on the list!</span>
      </motion.div>
    )
  }

  if (compact) {
    // Compact version for product cards
    return (
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()} className="w-full">
        <div className="flex gap-1">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email"
            required
            className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-pp-sand/50 bg-white focus:outline-none focus:ring-1 focus:ring-pp-sage"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-2 py-1.5 bg-pp-charcoal text-white text-xs rounded-lg hover:bg-pp-ink transition-colors disabled:opacity-50"
          >
            {status === 'loading' ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Mail className="w-3 h-3" />
            )}
          </button>
        </div>
        {status === 'error' && (
          <p className="text-pp-terracotta text-xs mt-1">{errorMessage}</p>
        )}
      </form>
    )
  }

  // Full version for product detail page
  return (
    <div className="space-y-3">
      <p className="text-pp-stone text-sm">
        Be the first to know when this product is available!
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pp-stone" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-pp-sand bg-white focus:outline-none focus:ring-2 focus:ring-pp-sage/50 focus:border-pp-sage"
          />
        </div>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="px-6 py-3 bg-pp-charcoal text-white font-medium rounded-xl hover:bg-pp-ink transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {status === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Notify Me'
          )}
        </button>
      </form>
      {status === 'error' && (
        <p className="text-pp-terracotta text-sm">{errorMessage}</p>
      )}
    </div>
  )
}
